import React, { useEffect, useState } from 'react';
import {
    Plus,
    Users,
    Calendar,
    FileText,
    MessageCircle,
    Search,
    TrendingUp,
    CheckCircle,
    User,
    Building,
    Mail,
    Award
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import EmailVerification from './EmailVerification';
import RecruiterCompleteProfile from './CompleteProfile';
import axios from 'axios'
import api from '../../utils/Api'
import { verifyAuth } from '../../store/authSlice';

import { setCredentials } from '../../store/authSlice';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {


    const navigate = useNavigate();

    const dispatch = useDispatch();
    const { user, token, isAuthenticated } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(true);
    const [statsData, setStatsData] = useState(null);
    const [recentActivity, setRecentActivity] = useState([]);
    const [quickStats, setQuickStats] = useState(null);
    const [subscriptions, setSubscriptions] = useState([]);

    const fetchProfileData = async () => {
        try {
            setIsLoading(true);
            const [profileRes, statsRes, subsRes] = await Promise.all([
                api.get(`/recruiter/profile`),
                api.get(`/recruiter/dashboard-stats`),
                api.get(`/recruiter/subscriptions`)
            ]);


            // Preserve existing auth fields (token, googleId, is_email_verified etc.)
            const updatedUser = {
                ...user,
                ...profileRes.data.recruiter
            };

            dispatch(setCredentials({ user: updatedUser, token }));

            setStatsData(statsRes.data.stats);
            //  console.log(statsRes)
            setRecentActivity(statsRes.data.recentActivity);
            setQuickStats(statsRes.data.quickStats);
            setSubscriptions(subsRes.data.subscriptions || []);

        } catch (error) {
            console.error("Erreur lors du chargement des données :", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const interval = setInterval(() => {
            dispatch(verifyAuth());
        }, 60000); // Check every minute

        return () => clearInterval(interval);
    }, [dispatch]);

    useEffect(() => {
        if (isAuthenticated && token) {
            fetchProfileData();
        } else {
            setIsLoading(false);
        }
    }, [isAuthenticated, token, dispatch]);


    // Show loading state while fetching user data
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border-l-4 border-blue-500">
                        <div className="animate-pulse">
                            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2"></div>
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                        </div>
                        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                            Chargement des données du profil...
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    if (!user?.is_email_verified && !user?.googleId) {
        return <EmailVerification />
    }

    // Only check profile completion after user data is loaded
    if (!user?.is_profile_complete) {
        return <RecruiterCompleteProfile />;
    }

    const quickLinks = [
        {
            title: "Créer une annonce",
            description: "Publier une nouvelle offre d'emploi",
            icon: Plus,
            color: "bg-blue-500 hover:bg-blue-600",
            action: () => navigate('/dashboard/new-offer')
        },
        {
            title: "Rechercher candidats",
            description: "Explorer la base de candidats",
            icon: Search,
            color: "bg-green-500 hover:bg-green-600",
            action: () => navigate('/dashboard/cvtheque')
        },
        {
            title: "Planifier entretien",
            description: "Programmer un nouvel entretien",
            icon: Calendar,
            color: "bg-purple-500 hover:bg-purple-600",
            action: () => navigate('/dashboard/calendar')
        },

    ];

    const stats = [
        {
            title: "Annonces actives",
            value: statsData?.activeJobs || 0,
            description: "Offres en ligne actuellement",
            icon: FileText,
            color: "text-blue-600 dark:text-blue-400",
            bgColor: "bg-blue-50 dark:bg-blue-900/20"
        },
        {
            title: "Candidatures reçues",
            value: statsData?.totalApplications || 0,
            description: "Total candidatures reçues",
            icon: Users,
            color: "text-green-600 dark:text-green-400",
            bgColor: "bg-green-50 dark:bg-green-900/20"
        },
        {
            title: "Entretiens programmés",
            value: statsData?.scheduledInterviews || 0,
            description: "Entretiens à venir",
            icon: Calendar,
            color: "text-purple-600 dark:text-purple-400",
            bgColor: "bg-purple-50 dark:bg-purple-900/20"
        },
        {
            title: "Candidats présélectionnés",
            value: statsData?.shortlisted || 0,
            description: "En cours d'évaluation",
            icon: CheckCircle,
            color: "text-orange-600 dark:text-orange-400",
            bgColor: "bg-orange-50 dark:bg-orange-900/20"
        }
    ];

    const getActivityIcon = (type) => {
        switch (type) {
            case 'application': return User;
            case 'interview': return Calendar;
            case 'job': return FileText;
            default: return FileText;
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return "À l'instant";
        if (diffInSeconds < 3600) return `Il y a ${Math.floor(diffInSeconds / 60)} min`;
        if (diffInSeconds < 86400) return `Il y a ${Math.floor(diffInSeconds / 3600)} h`;
        return `Il y a ${Math.floor(diffInSeconds / 86400)} j`;
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border-l-4 border-blue-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                    Bienvenue, {user?.company_name}
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400">
                                    Gérez vos offres d'emploi et suivez vos recrutements - {user?.company}
                                </p>
                                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                    <Mail className="inline w-4 h-4 mr-1" />
                                    {user?.email}
                                </div>
                            </div>
                            <div className="flex items-center space-x-4">
                                <div className="text-right">
                                    <div className="text-sm text-gray-500 dark:text-gray-400">Taux de réussite</div>
                                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">73%</div>
                                </div>
                                <TrendingUp className="w-12 h-12 text-green-500 dark:text-green-400" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Profile Status */}
                <div className="mb-6">
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                        <div className="flex items-center">
                            <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mr-2" />
                            <span className="text-sm font-medium text-green-800 dark:text-green-300">
                                Profil complet - Toutes les fonctionnalités sont disponibles
                            </span>
                        </div>
                    </div>
                </div>

                {/* Active Packs / Subscriptions */}
                {subscriptions.length > 0 ? (
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Mes Packs Actifs</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {subscriptions.map((sub) => (
                                <div key={sub._id} className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-lg p-6 text-white relative overflow-hidden">
                                    <div className="absolute top-0 right-0 p-4 opacity-20">
                                        <Award className="w-24 h-24" />
                                    </div>
                                    <div className="relative z-10">
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <h3 className="text-lg font-bold">{sub.pack?.name || 'Pack Inconnu'}</h3>
                                                <p className="text-sm opacity-90">Expire le {new Date(sub.endDate).toLocaleDateString()}</p>
                                            </div>
                                            <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-medium">
                                                {sub.status === 'active' ? 'Actif' : 'Expiré'}
                                            </span>
                                        </div>

                                        <div className="space-y-3">
                                            <div>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span>Crédits Annonces</span>
                                                    <span className="font-bold">{sub.adCredits - sub.adCreditsUsed} / {sub.adCredits}</span>
                                                </div>
                                                <div className="w-full bg-black/20 rounded-full h-2">
                                                    <div
                                                        className="bg-white h-2 rounded-full transition-all duration-500"
                                                        style={{ width: `${Math.min(((sub.adCreditsUsed) / sub.adCredits) * 100, 100)}%` }} // Inverted logic for "used" vs "remaining" bar? usually bar shows usage or remaining. Let's show usage.
                                                    ></div>
                                                </div>
                                            </div>
                                            <div>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span>Crédits CVthèque</span>
                                                    <span className="font-bold">
                                                        {sub.pack?.cvCount === -1 ? 'Illimité' : `${sub.cvCredits - sub.cvCreditsUsed} / ${sub.cvCredits}`}
                                                    </span>
                                                </div>
                                                {sub.pack?.cvCount !== -1 && (
                                                    <div className="w-full bg-black/20 rounded-full h-2">
                                                        <div
                                                            className="bg-white h-2 rounded-full transition-all duration-500"
                                                            style={{ width: `${Math.min(((sub.cvCreditsUsed) / sub.cvCredits) * 100, 100)}%` }}
                                                        ></div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="mb-8">
                        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6 flex items-center justify-between">
                            <div className="flex items-center">
                                <TrendingUp className="w-8 h-8 text-orange-500 mr-4" />
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-300">Aucun pack actif</h3>
                                    <p className="text-orange-700 dark:text-orange-400">Boostez vos recrutements en choisissant un pack adapté à vos besoins.</p>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate('/services')}
                                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors font-medium shadow-sm"
                            >
                                Voir les offres
                            </button>
                        </div>
                    </div>
                )}

                {/* Quick Actions */}
                <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Actions rapides</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {quickLinks.map((link, index) => {
                            const IconComponent = link.icon;
                            return (
                                <button
                                    key={index}
                                    onClick={link.action}
                                    className={`${link.color} text-white p-6 rounded-lg shadow-md transition-all duration-200 hover:shadow-lg hover:scale-105 text-left`}
                                >
                                    <div className="flex items-center mb-3">
                                        <IconComponent className="w-6 h-6 mr-3" />
                                        <h3 className="font-semibold text-lg">{link.title}</h3>
                                    </div>
                                    <p className="text-sm opacity-90">{link.description}</p>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Statistics */}
                <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Statistiques</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {stats.map((stat, index) => {
                            const IconComponent = stat.icon;
                            return (
                                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                                                {stat.value}
                                            </div>
                                            <div className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                {stat.title}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                {stat.description}
                                            </div>
                                        </div>
                                        <div className={`${stat.bgColor} p-3 rounded-full`}>
                                            <IconComponent className={`w-6 h-6 ${stat.color}`} />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Activité récente</h3>
                            <div className="space-y-4">
                                {recentActivity?.length > 0 ? (
                                    recentActivity.map((activity, index) => {
                                        const IconComponent = getActivityIcon(activity.type);
                                        return (
                                            <div key={index} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                                <div className="bg-blue-100 dark:bg-blue-900/50 p-2 rounded-full">
                                                    <IconComponent className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-900 dark:text-white">{activity.message}</p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formatTime(activity.time)}</p>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <p className="text-gray-500 dark:text-gray-400 text-sm">Aucune activité récente.</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {/* Quick Stats */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Aperçu rapide</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Candidatures aujourd'hui</span>
                                    <span className="font-semibold text-blue-600 dark:text-blue-400">{quickStats?.applicationsToday || 0}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Entretiens demain</span>
                                    <span className="font-semibold text-green-600 dark:text-green-400">{quickStats?.interviewsTomorrow || 0}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Offres expirées</span>
                                    <span className="font-semibold text-red-600 dark:text-red-400">{quickStats?.expiredOffers || 0}</span>
                                </div>
                            </div>
                        </div>

                        {/* User Info Panel */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Informations utilisateur</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <User className="w-4 h-4 mr-2" />
                                    <span>{user?.first_name} {user?.last_name}</span>
                                </div>
                                <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <Building className="w-4 h-4 mr-2" />
                                    <span>{user?.company_name}</span>
                                </div>
                                <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <Mail className="w-4 h-4 mr-2" />
                                    <span>{user?.email}</span>
                                </div>
                            </div>
                        </div>

                        {/* Action Panel */}
                        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-md p-6 text-white">
                            <h3 className="text-lg font-semibold mb-2">Centre d'aide</h3>
                            <p className="text-sm opacity-90 mb-4">
                                Besoin d'aide pour optimiser vos recrutements ?
                            </p>
                            <button className="bg-white text-blue-600 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors">
                                Contacter le support
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;